import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Col, Row } from 'react-bootstrap'
import {
  AffiliationUsersTable,
  DenyRequestModal,
  SearchInput,
  SearchSplash
} from '../../components'
import {
  useAffiliationUserMutation,
  useAffiliationUsersExportMutation,
  useAffiliationUsersSearch
} from '../../hooks'
import { useActions } from '../../store'
import { faCloudDownloadAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PAGE_STATE, PERMISSION, UserType } from '../../helpers'

const AffiliationUsersList = ({ userType }) => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState([])
  const [showDenyRequestModal, setShowDenyRequestModal] = useState(false)
  const [shouldRefetch, setShouldRefetch] = useState(false)
  const { progressStart, progressEnd, setPageState } = useActions()
  const userArgsRef = useRef()

  const { users, pageCount, refetch } = useAffiliationUsersSearch({
    search,
    userType,
    page,
    sort
  })
  const { mutate: exportUsers } = useAffiliationUsersExportMutation()
  const { mutate: updateUser } = useAffiliationUserMutation()

  const noResultText =
    userType === 'pending'
      ? 'There is no new user request'
      : 'Sorry, no results found'

  useEffect(() => {
    setPageState(users?.length ? PAGE_STATE.EDIT : PAGE_STATE.DEFAULT)
  }, [users?.length, setPageState])

  // Reset state when userType changes
  useEffect(() => {
    setPage(1)
    setSort([])
    setSearch('')
    if (userType !== UserType.All) {
      setShouldRefetch(true)
    }
  }, [userType])

  // Refetch when explicitly triggered
  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false)
      progressStart()
      refetch().finally(() => progressEnd())
    }
  }, [shouldRefetch, progressStart, progressEnd, refetch])

  const handleExport = () => exportUsers({ search, sort, userType })

  const handleSearch = () => {
    setPage(1)
    setSort([])
    setShouldRefetch(true)
  }

  const handlePageChange = (data) => {
    const newPage = data.selected + 1
    if (newPage !== page) {
      setPage(newPage)
      setShouldRefetch(true)
    }
  }

  const handleSort = (data) => {
    if (data !== sort) {
      setPage(1)
      setSort(data)
      setShouldRefetch(true)
    }
  }

  const handleUserUpdate = () =>
    updateUser(userArgsRef.current, {
      onSuccess: () => {
        const user = users.find((user) => user.id === userArgsRef.current.id)
        user[userArgsRef.current.property] = userArgsRef.current.value
      }
    })

  const handleUpdate = (id, property, value) => {
    userArgsRef.current = {
      id,
      property,
      value
    }
    if (property === 'permission' && value === PERMISSION.DENIED) {
      setShowDenyRequestModal(true)
    } else {
      handleUserUpdate()
    }
  }

  const handleDenyRequestModalCancel = () => setShowDenyRequestModal(false)

  const handleDenyRequestModalConfirm = (args) => {
    setShowDenyRequestModal(false)
    userArgsRef.current = { ...userArgsRef.current, extra: args }
    handleUserUpdate()
  }

  return (
    <>
      <Row className='pb-3'>
        <Col xs={12} sm={8} md={6} lg={4}>
          <SearchInput
            placeholder='Search by name or email'
            search={search}
            onSearchChange={setSearch}
            onSearch={handleSearch}
          />
        </Col>
        <Col className='text-right'>
          <Button
            className='pl-0 pr-1 text-dark'
            onClick={handleExport}
            title='Export Data'
            variant='link'
          >
            <FontAwesomeIcon icon={faCloudDownloadAlt} /> Export Data
          </Button>
        </Col>
      </Row>
      {users ? (
        users.length ? (
          <AffiliationUsersTable
            users={users}
            page={page - 1}
            pageCount={pageCount}
            onPageChange={handlePageChange}
            sort={sort}
            onSort={handleSort}
            onUpdate={handleUpdate}
          />
        ) : (
          <SearchSplash text={noResultText} />
        )
      ) : (
        <SearchSplash text='Find a user among all registered users by searching for their name, username, or email' />
      )}
      <DenyRequestModal
        show={showDenyRequestModal}
        onCancel={handleDenyRequestModalCancel}
        onConfirm={handleDenyRequestModalConfirm}
      />
    </>
  )
}

AffiliationUsersList.propTypes = {
  userType: PropTypes.oneOf(Object.values(UserType))
}

export { AffiliationUsersList }
